import { useEffect, useRef, useState } from "react";
import { Building2, ImagePlus, Trash2, X } from "lucide-react";
import { api, API } from "@/lib/api";
import UsersSettings from "@/pages/UsersSettings";
import MappingSettings from "@/pages/MappingSettings";

const TABS = [
  { id: "profil", label: "Profil Perusahaan" },
  { id: "pengguna", label: "Pengaturan Pengguna" },
  { id: "pemetaan", label: "Pemetaan Akun" },
];

export default function Settings() {
  const [tab, setTab] = useState("profil");
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoVersion, setLogoVersion] = useState(Date.now());
  const fileRef = useRef(null);
  useEffect(() => {
    let active = true;
    api("/company").then((c) => active && setForm((f) => f ?? c)).catch((e) => active && setMessage({ text: e.message, type: "error" }));
    return () => { active = false; };
  }, []);
  if (!form) return message
    ? <div className="surface access-denied" data-testid="settings-error"><h2>Tidak dapat memuat pengaturan</h2><p className="muted">{message.text}</p></div>
    : <div className="report-loading" data-testid="settings-loading">Memuat pengaturan...</div>;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const notify = (text, type = "success") => { setMessage({ text, type }); setTimeout(() => setMessage(null), 6000); };
  const save = async () => {
    setSaving(true);
    try {
      const updated = await api("/company", { method: "PATCH", body: JSON.stringify({ name: form.name, address: form.address, phone: form.phone, email: form.email, npwp: form.npwp, business_type: form.business_type, city: form.city }) });
      setForm(updated);
      notify("Profil perusahaan berhasil disimpan.");
    } catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return notify("Ukuran logo maksimal 2 MB", "error");
    const data = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
    try {
      await api("/company/logo", { method: "POST", body: JSON.stringify({ filename: file.name, mime: file.type, data }) });
      setForm({ ...form, has_logo: true });
      setLogoVersion(Date.now());
      notify("Logo berhasil diunggah — akan tampil di kop PDF invoice & tagihan.");
    } catch (e) { notify(e.message, "error"); }
  };
  const removeLogo = async () => {
    try {
      await api("/company/logo", { method: "DELETE" });
      setForm({ ...form, has_logo: false });
      notify("Logo dihapus.");
    } catch (e) { notify(e.message, "error"); }
  };
  return (
    <div data-testid="settings-page">
      <div className="page-heading">
        <div><p className="eyebrow">PENGATURAN</p><h1>Pengaturan</h1><p className="muted">Profil perusahaan, pengguna & peran akses, dan pemetaan akun.</p></div>
      </div>
      <div className="contact-tabs" data-testid="settings-tabs">
        {TABS.map((t) => <button key={t.id} data-testid={`settings-tab-${t.id}`} className={`contact-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>
      {message && <div data-testid="settings-message" className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
      {tab === "pengguna" && <UsersSettings notify={notify} />}
      {tab === "pemetaan" && <MappingSettings notify={notify} />}
      {tab === "profil" && (<>
      <div className="surface tax-form" data-testid="settings-logo-section">
        <h3><ImagePlus size={15} /> Logo perusahaan</h3>
        <div className="logo-row">
          {form.has_logo
            ? <img data-testid="settings-logo-preview" className="logo-preview" src={`${API}/company/logo?v=${logoVersion}`} alt="Logo perusahaan" />
            : <div className="logo-placeholder" data-testid="settings-logo-placeholder"><Building2 size={22} /><span>Belum ada logo</span></div>}
          <div className="logo-actions">
            <button data-testid="settings-logo-upload" className="secondary-btn small" onClick={() => fileRef.current?.click()}><ImagePlus size={14} /> {form.has_logo ? "Ganti logo" : "Unggah logo"}</button>
            {form.has_logo && <button data-testid="settings-logo-delete" className="danger-btn small" onClick={removeLogo}><Trash2 size={14} /> Hapus</button>}
            <p className="muted">PNG / JPG / WEBP, maksimal 2 MB. Disarankan latar transparan.</p>
          </div>
          <input ref={fileRef} data-testid="settings-logo-input" type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { uploadLogo(e.target.files[0]); e.target.value = ""; }} />
        </div>
      </div>
      <div className="surface tax-form" data-testid="settings-profile-section">
        <h3><Building2 size={15} /> Profil perusahaan</h3>
        <div className="tax-form-grid">
          <label>Nama perusahaan<input data-testid="settings-company-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></label>
          <label>NPWP<input data-testid="settings-company-npwp" value={form.npwp || ""} onChange={(e) => set("npwp", e.target.value)} placeholder="Opsional" /></label>
          <label>Telepon<input data-testid="settings-company-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="mis. 021-555-0123" /></label>
          <label>Email<input data-testid="settings-company-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="mis. finance@perusahaan.co.id" /></label>
          <label>Jenis usaha<input data-testid="settings-company-business" value={form.business_type || ""} onChange={(e) => set("business_type", e.target.value)} placeholder="mis. Perdagangan / Ritel" /></label>
          <label>Kota<input data-testid="settings-company-city" value={form.city || ""} onChange={(e) => set("city", e.target.value)} placeholder="mis. Jakarta Selatan" /></label>
          <label className="span-2">Alamat<input data-testid="settings-company-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Alamat lengkap untuk kop invoice" /></label>
        </div>
        <div className="tax-form-actions">
          <button data-testid="settings-save-button" className="primary-btn" disabled={!form.name?.trim() || saving} onClick={save}>{saving ? "Menyimpan..." : "Simpan Profil"}</button>
        </div>
      </div>
      </>)}
    </div>
  );
}
